/**
 * AzureConfigSettings - Admin UI for Azure AD configuration
 * 
 * Provides configuration management for Azure AD integration:
 * - Form for Azure AD configuration (tenant ID, client ID, client secret)
 * - Test connectivity button
 * - Cache TTL configuration
 * 
 * Requirements: 8.1, 8.3, 8.4, 4.5
 */
import React, { useState, useEffect } from 'react';
import {
  Cloud,
  Save,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Eye,
  EyeOff,
  TestTube,
  Clock,
  Shield,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { toast } from 'react-hot-toast';
import { apiService } from '../services/apiService';

// TTL bounds (in seconds)
const MIN_TTL_SECONDS = 60;      // 1 minute
const MAX_TTL_SECONDS = 86400;   // 24 hours

// TTL presets for easy selection
const TTL_PRESETS = [
  { label: '5 minutes', value: 300 },
  { label: '15 minutes', value: 900 },
  { label: '30 minutes', value: 1800 },
  { label: '1 hour', value: 3600 },
  { label: '4 hours', value: 14400 },
  { label: '24 hours', value: 86400 },
];

const AzureConfigSettings = () => {
  const [config, setConfig] = useState({
    tenant_id: '',
    client_id: '',
    client_secret: '',
    graph_api_enabled: true,
    cache_ttl_seconds: 300,
  });
  const [originalConfig, setOriginalConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [configExists, setConfigExists] = useState(false);

  // Load config on mount
  useEffect(() => {
    loadConfig();
  }, []);

  // Track changes
  useEffect(() => {
    if (originalConfig) {
      const changed =
        config.tenant_id !== originalConfig.tenant_id ||
        config.client_id !== originalConfig.client_id ||
        config.client_secret !== '' ||
        config.graph_api_enabled !== originalConfig.graph_api_enabled ||
        config.cache_ttl_seconds !== originalConfig.cache_ttl_seconds;
      setHasChanges(changed);
    }
  }, [config, originalConfig]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAzureConfig();
      
      if (response && response.tenant_id) {
        setConfigExists(true);
        const loadedConfig = {
          tenant_id: response.tenant_id || '',
          client_id: response.client_id || '',
          client_secret: '', // Never pre-fill secret
          graph_api_enabled: response.graph_api_enabled ?? true,
          cache_ttl_seconds: response.cache_ttl_seconds || 300,
        };
        setConfig(loadedConfig);
        setOriginalConfig({
          ...loadedConfig,
          client_secret: '', // Track that secret wasn't changed
        });
      } else {
        setConfigExists(false);
        setOriginalConfig(null);
      }
    } catch (error) {
      if (error.message?.includes('404') || error.message?.includes('not found')) {
        setConfigExists(false);
        setOriginalConfig(null);
      } else {
        toast.error('Failed to load Azure AD configuration');
        console.error('Error loading config:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validate required fields
    if (!config.tenant_id.trim()) {
      toast.error('Tenant ID is required');
      return;
    }
    if (!config.client_id.trim()) {
      toast.error('Client ID is required');
      return;
    }
    if (!configExists && !config.client_secret.trim()) {
      toast.error('Client Secret is required for new configuration');
      return;
    }

    // Validate TTL bounds
    if (config.cache_ttl_seconds < MIN_TTL_SECONDS || config.cache_ttl_seconds > MAX_TTL_SECONDS) {
      toast.error(`Cache TTL must be between ${MIN_TTL_SECONDS} and ${MAX_TTL_SECONDS} seconds`);
      return;
    }

    try {
      setSaving(true);
      
      const payload = {
        tenant_id: config.tenant_id.trim(),
        client_id: config.client_id.trim(),
        graph_api_enabled: config.graph_api_enabled,
        cache_ttl_seconds: config.cache_ttl_seconds,
      };

      // Only include secret if it was changed
      if (config.client_secret.trim()) {
        payload.client_secret = config.client_secret;
      }

      await apiService.updateAzureConfig(payload);
      toast.success('Azure AD configuration saved successfully');
      
      // Reload to get updated config
      await loadConfig();
      setTestResult(null);
    } catch (error) {
      const message = error.message || 'Failed to save configuration';
      toast.error(message);
      console.error('Error saving config:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnectivity = async () => {
    try {
      setTesting(true);
      setTestResult(null);
      
      const result = await apiService.testAzureConnectivity();
      setTestResult(result);
      
      if (result.success) {
        toast.success('Successfully connected to Azure AD');
      } else {
        toast.error(result.message || 'Connection test failed');
      }
    } catch (error) {
      const message = error.message || 'Failed to test connectivity';
      setTestResult({
        success: false,
        message: message,
      });
      toast.error(message);
      console.error('Error testing connectivity:', error);
    } finally {
      setTesting(false);
    }
  };

  const handleReset = () => {
    if (originalConfig) {
      setConfig({
        ...originalConfig,
        client_secret: '',
      });
    }
    setTestResult(null);
  };

  const formatTTL = (seconds) => {
    if (seconds < 60) return `${seconds} seconds`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours`;
    return `${Math.floor(seconds / 86400)} days`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Cloud className="w-5 h-5" />
            Azure AD Configuration
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Configure Azure AD integration for authentication
          </p>
        </div>
        <div className="flex gap-2">
          {hasChanges && (
            <Button variant="outline" onClick={handleReset} disabled={saving}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Reset
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving || !hasChanges}>
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Status Alert */}
      {!configExists && (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800">Configuration Required</AlertTitle>
          <AlertDescription className="text-amber-700">
            Azure AD is not configured. Enter your Azure AD credentials below to enable
            group-based access control.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Azure AD Credentials */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Azure AD Credentials
            </CardTitle>
            <CardDescription>
              Enter your Azure AD app registration details
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="tenant_id">
                Tenant ID <span className="text-red-500">*</span>
              </Label>
              <Input
                id="tenant_id"
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                value={config.tenant_id}
                onChange={(e) => setConfig({ ...config, tenant_id: e.target.value })}
              />
              <p className="text-xs text-gray-500">
                Your Azure AD tenant ID (Directory ID)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="client_id">
                Client ID <span className="text-red-500">*</span>
              </Label>
              <Input
                id="client_id"
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                value={config.client_id}
                onChange={(e) => setConfig({ ...config, client_id: e.target.value })}
              />
              <p className="text-xs text-gray-500">
                Application (client) ID from your app registration
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="client_secret">
                Client Secret {!configExists && <span className="text-red-500">*</span>}
              </Label>
              <div className="relative">
                <Input
                  id="client_secret"
                  type={showSecret ? 'text' : 'password'}
                  placeholder={configExists ? '••••••••' : 'Enter client secret'}
                  value={config.client_secret}
                  onChange={(e) => setConfig({ ...config, client_secret: e.target.value })}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowSecret(!showSecret)}
                >
                  {showSecret ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                {configExists
                  ? 'Leave blank to keep existing secret, or enter a new value to update'
                  : 'Client secret from your app registration'}
              </p>
            </div>

            <div className="flex items-center justify-between pt-2">
              <div>
                <Label htmlFor="graph_api_enabled">Microsoft Graph API</Label>
                <p className="text-xs text-gray-500">
                  Enable for group lookup when groups exceed token limit
                </p>
              </div>
              <Switch
                id="graph_api_enabled"
                checked={config.graph_api_enabled}
                onCheckedChange={(checked) =>
                  setConfig({ ...config, graph_api_enabled: checked })
                }
              />
            </div>
          </CardContent>
        </Card>

        {/* Cache Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Cache Settings
            </CardTitle>
            <CardDescription>
              Configure role cache time-to-live (TTL)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cache_ttl">
                Cache TTL (seconds)
              </Label>
              <Input
                id="cache_ttl"
                type="number"
                min={MIN_TTL_SECONDS}
                max={MAX_TTL_SECONDS}
                value={config.cache_ttl_seconds}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    cache_ttl_seconds: parseInt(e.target.value, 10) || 300,
                  })
                }
              />
              <p className="text-xs text-gray-500">
                Current: {formatTTL(config.cache_ttl_seconds)} (min: 1 minute, max: 24 hours)
              </p>
            </div>

            <div className="space-y-2">
              <Label>Quick Presets</Label>
              <div className="flex flex-wrap gap-2">
                {TTL_PRESETS.map((preset) => (
                  <Button
                    key={preset.value}
                    variant={config.cache_ttl_seconds === preset.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() =>
                      setConfig({ ...config, cache_ttl_seconds: preset.value })
                    }
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
            </div>

            <Alert className="mt-4">
              <Clock className="h-4 w-4" />
              <AlertTitle>About Role Caching</AlertTitle>
              <AlertDescription className="text-sm">
                User roles are cached to improve performance. A shorter TTL means more
                frequent Azure AD queries but faster role updates. A longer TTL reduces
                Azure AD load but delays role changes.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>

      {/* Connectivity Test */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TestTube className="w-5 h-5" />
            Connectivity Test
          </CardTitle>
          <CardDescription>
            Verify your Azure AD configuration is working correctly
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Button
              onClick={handleTestConnectivity}
              disabled={testing || !configExists}
              variant="outline"
            >
              {testing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <TestTube className="w-4 h-4 mr-2" />
                  Test Connection
                </>
              )}
            </Button>
            
            {!configExists && (
              <p className="text-sm text-gray-500">
                Save configuration first to test connectivity
              </p>
            )}
          </div>

          {testResult && (
            <div className="mt-4">
              {testResult.success ? (
                <Alert className="border-green-200 bg-green-50">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertTitle className="text-green-800">Connection Successful</AlertTitle>
                  <AlertDescription className="text-green-700">
                    {testResult.message}
                    {testResult.details && (
                      <div className="mt-2 text-xs">
                        <p>Token Type: {testResult.details.token_type}</p>
                        <p>Expires In: {testResult.details.expires_in} seconds</p>
                      </div>
                    )}
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert className="border-red-200 bg-red-50">
                  <XCircle className="h-4 w-4 text-red-600" />
                  <AlertTitle className="text-red-800">Connection Failed</AlertTitle>
                  <AlertDescription className="text-red-700">
                    {testResult.message}
                    {testResult.details && testResult.details.azure_error && (
                      <div className="mt-2 text-xs">
                        <p>Error: {testResult.details.azure_error}</p>
                        <p>Description: {testResult.details.azure_error_description}</p>
                      </div>
                    )}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Unsaved Changes Warning */}
      {hasChanges && (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800">Unsaved Changes</AlertTitle>
          <AlertDescription className="text-amber-700">
            You have unsaved configuration changes. Click "Save Changes" to apply them.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default AzureConfigSettings;
